import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function SetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then(({ data }) => setInfo(data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/set-password", { token, password });
      await login({ email: info.email, password });
      navigate(info.role === "driver" ? "/portal/driver" : info.role === "client" ? "/portal/client" : "/app");
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div data-testid="set-password-page" className="flex min-h-screen items-center justify-center bg-ink-900 px-4 text-slate-200">
      <div className="w-full max-w-md rise">
        <p className="font-display text-lg font-semibold"><span className="gold-text">{PLATFORM_NAME}</span></p>
        {info ? (
          <>
            <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-50">Set your password</h1>
            <p className="mt-3 text-sm text-slate-400">
              {info.company_name} invited you as a <span className="text-gold">{info.role}</span>. Your sign-in email
              is {info.email}.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6">
              <div>
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">New password</label>
                <Input data-testid="set-password-input" type="password" required minLength={8} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 characters"
                  className="h-12 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
              </div>
              {error && <p data-testid="set-password-error" className="text-sm text-red-400">{error}</p>}
              <Button data-testid="set-password-submit" type="submit" disabled={busy} className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save and enter my portal"}
              </Button>
            </form>
          </>
        ) : (
          <div className="mt-10">
            {error ? (
              <>
                <p data-testid="set-password-invalid" className="text-sm text-red-400">{error}</p>
                <Link to="/login" className="mt-4 inline-block text-sm text-gold">Go to sign in</Link>
              </>
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-gold" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
